import React from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import { ArrowForwardIosRounded as ChevronIcon } from '@mui/icons-material';
import { status as statusTokens } from '../../theme';
import { transitions, pressable } from '../../motion';

export type StatTone = 'neutral' | 'ok' | 'warn' | 'danger';

const toneColor: Record<StatTone, string | undefined> = {
  neutral: undefined,
  ok: statusTokens.ok,
  warn: statusTokens.warn,
  danger: statusTokens.danger,
};

/**
 * One number, what it counts, and where to go to act on it.
 *
 * The tone is deliberately restrained: a tile is only coloured when the
 * number is a problem, so a healthy overview is a calm page of black
 * numerals and the one red tile is the thing you look at. A dashboard where
 * every tile is coloured tells you nothing.
 *
 * `value === null` renders "—", never 0 — the same rule the vault applies
 * to a null compliance score. Not loaded is not the same as none.
 */
const StatTile: React.FC<{
  label: string;
  value: number | string | null;
  /** One line, or a few (a node) when the tile has two facts to give. */
  hint?: React.ReactNode;
  /** A change against last time, shown at the right of the number. */
  trend?: React.ReactNode;
  tone?: StatTone;
  onClick?: () => void;
  loading?: boolean;
}> = ({ label, value, hint, trend, tone = 'neutral', onClick, loading }) => {
  const color = toneColor[tone];
  const interactive = !!onClick;

  return (
    <Card
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick!(); } }
          : undefined
      }
      sx={{
        // Fills its grid cell, so tiles in a row are one height whatever
        // their hints (a second line, none at all) or wrappers.
        p: 2, minWidth: 0, height: '100%',
        cursor: interactive ? 'pointer' : 'default',
        transition: transitions(['border-color', 'box-shadow']),
        ...(interactive && {
          // A tile lit up on hover and then did nothing at all when clicked.
          // On a touchscreen there is no hover, so until this it gave no
          // feedback of any kind between the tap and the next page arriving.
          ...pressable,
          '&:hover': {
            borderColor: 'grey.300',
            boxShadow: '0 2px 8px rgba(22,21,15,0.06)',
            '& .stat-chevron': { opacity: 1, transform: 'translateX(0)' },
          },
        }),
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', minWidth: 0 }}>
          {label}
        </Typography>
        {interactive && (
          <ChevronIcon
            className="stat-chevron"
            sx={{
              fontSize: 11, color: 'text.disabled', mt: 0.4,
              opacity: 0, transform: 'translateX(-3px)',
              transition: transitions(['opacity', 'transform']),
            }}
          />
        )}
      </Stack>

      {/* The number, and what it did since last time at the far end of
          the same row, so every tile carries its trend in one place. When
          the tile is too narrow for both, the trend drops under the number
          rather than running into it. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" columnGap={1.5} rowGap={0.25} sx={{ mt: 0.5 }}>
        <Typography
          sx={{
            fontSize: '1.875rem', fontWeight: 800, lineHeight: 1.1, flexShrink: 0, whiteSpace: 'nowrap',
            letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums',
            color: loading || value === null ? 'text.disabled' : color || 'text.primary',
          }}
        >
          {loading ? '—' : value === null ? '—' : value}
        </Typography>
        {trend && !loading && <Box sx={{ minWidth: 0, textAlign: 'right', ml: 'auto' }}>{trend}</Box>}
      </Stack>

      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {hint}
        </Typography>
      )}

      {!hint && <Box sx={{ height: 17, mt: 0.25 }} />}
    </Card>
  );
};

export default StatTile;
